import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTokens } from '../../shared/theme';
import { Button } from '../../shared/uiKit';
import Spinner from '../../shared/Spinner';
import { tenantSlugFromHostname } from '../../shared/validators';
import { notifyError } from '../../shared/toast.js';
import { acceptInvite } from '../../shared/services/institutions';
import {
  buildPortalAuthUrl,
  navigateToPortalAuth,
  signout,
} from '../../shared/services/auth';
import StateScreen from '../StateScreen';
import { resolvePendingInvite, stashPendingInvite } from '../pendingInvite.js';

/**
 * Institution-admin invite acceptance (/accept?token=…). Modeled on the RN
 * accept route, plus the signed-out flow:
 *   1. Try acceptInvite(token). A 401 means signed out; an email-mismatch 403
 *      means a different portal account owns the current session.
 *   2. Clear a mismatched portal session, stash the token, and Google-hop.
 *   3. Google returns to the school root, which forwards to /accept; restore
 *      the token from the same-origin stash and retry acceptInvite.
 *
 * Loop-safe: the stash is consumed on the return leg, so another auth failure
 * surfaces an error instead of hopping forever.
 *
 */
export default function AcceptInvite() {
  const t = useTokens();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlToken = params.get('token');
  const [state, setState] = useState('working'); // working | ok | error
  const [message, setMessage] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) { return; }
    ran.current = true;

    const { token, returning } = resolvePendingInvite(urlToken);
    if (!token) {
      setState('error');
      setMessage('Missing invitation token.');
      return;
    }

    (async () => {
      try {
        await acceptInvite(String(token));
        setState('ok');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 410) {
          // Expired/revoked invites do not justify replacing a valid portal
          // session. Return to the account that is already signed in, and
          // say why so the invitee asks for a new link.
          const reason = String(err?.response?.data?.error || 'This invitation has expired').replace(/\.$/, '');
          notifyError(`${reason}. Ask an owner to send a new one.`);
          navigate('/', { replace: true });
          return;
        }
        const accountMismatch = status === 403
          && err?.response?.data?.code === 'INVITE_EMAIL_MISMATCH';
        if ((status === 401 || accountMismatch) && !returning) {
          // A signed-out visitor or a stale session for another account needs
          // the Google account chooser. The invite is preserved on this school
          // origin and resumed after the shared auth hop returns.
          const slug = tenantSlugFromHostname();
          if (!slug) {
            setState('error');
            setMessage('Sign in to accept this invitation.');
            return;
          }
          if (accountMismatch) {
            try {
              await signout();
            } catch {
              setState('error');
              setMessage('Could not switch accounts. Please try again.');
              return;
            }
          }
          try {
            stashPendingInvite(token);
          } catch { /* sessionStorage unavailable — hop anyway */ }
          // Invitation tokens stay on the tenant origin; auth only needs the
          // school hostname because the token is already stashed locally.
          navigateToPortalAuth(buildPortalAuthUrl(), { preservePath: false });
          return;
        }
        setState('error');
        setMessage(err?.response?.data?.error || 'This invitation is invalid or expired.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToPortal = () => { window.location.href = '/'; };

  return (
    <StateScreen>
      <h1
        className="text-[28px] leading-tight mb-4"
        style={{ fontFamily: t.fonts.display, fontWeight: 500, color: t.heading }}
      >
        Institution invite
      </h1>

      {state === 'working' ? (
        <div className="flex items-center gap-3">
          <Spinner size={22} />
          <span style={{ color: t.muted, fontFamily: t.fonts.body }}>Accepting your invitation…</span>
        </div>
      ) : null}

      {state === 'ok' ? (
        <div>
          <h2
            className="text-lg mb-1"
            style={{ fontFamily: t.fonts.display, fontWeight: 500, color: t.heading }}
          >
            You&apos;re an admin now
          </h2>
          <p className="text-base mb-4" style={{ color: t.muted, fontFamily: t.fonts.body }}>
            Your invitation was accepted. You can now manage your school.
          </p>
          <Button label="Go to portal" onClick={goToPortal} />
        </div>
      ) : null}

      {state === 'error' ? (
        <div>
          <h2
            className="text-lg mb-1"
            style={{ fontFamily: t.fonts.display, fontWeight: 500, color: t.heading }}
          >
            Couldn&apos;t accept invitation
          </h2>
          <p className="text-base mb-4" style={{ color: t.muted, fontFamily: t.fonts.body }}>
            {message}
          </p>
          <Button label="Go to portal" variant="secondary" onClick={goToPortal} />
        </div>
      ) : null}
    </StateScreen>
  );
}
